<template>
  <div class="download-container">
    <!-- Loader with Hourglass Icon -->
    <v-overlay :value="isLoading" class="d-flex flex-column align-center justify-center">
      <img src="../assets/img/loader.gif" class="loader-gif" style="width: 220px; height: 220px;"/>
      <p class="loading-text mt-2">{{ loadingMessage }}</p>
    </v-overlay>

    <!-- Success Message -->
    <div v-if="!isLoading && downloadComplete" class="success-message">
      <v-icon size="128" color="success">mdi-check-circle</v-icon>
      <p class="success-text">
        Your file has been successfully downloaded.
      </p>
      <v-btn color="primary" class= "cust-btn" @click="closeTab">
        Close Tab
      </v-btn>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import constants from "@/utils/constants.js";

export default {
  name: "TestReports",
  async mounted() {
    try {
      const { direct, testData } = this.$route.query;
      if (!direct || !testData) {
        throw new Error("Invalid query parameters. 'direct' and 'testData' are required.");
      }

      console.log("Received query params:", { direct, testData });
      // Additional query parameters
      const additionalParams = {
        custom: 'offline',
        column: 2,
        isRoughWork: false,
        pageBreak: true,
      };
      // Construct additional parameters as a query string
      const additionalQueryString = Object.entries(additionalParams)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');

      // Construct the new URL with encoded testData and additional parameters
      this.newUrl = `${process.env.VUE_APP_API_URL}/test/${direct}?testData=${encodeURIComponent(testData)}&${additionalQueryString}`;
      console.log("Constructed URL:", this.newUrl);
     

      this.downloadStatus = constants.DOWNLOAD_STATUS;

      // Trigger the download
      this.downloadTestResource(this.newUrl);
    } catch (error) {
      console.error("Error during component initialization:", error);
    }
  },
  data() {
    return {
      isLoading: true,
      loadingMessage: "Preparing your file for download. Please do not close the tab.",
      downloadComplete: false,
      requestIds: [],
      testStatusTimeout: null,
      newUrl: null,
    };
  },
  methods: {
    ...mapActions({
      downloadTestPaper: "testStore/downloadTest",
      deleteDownloadData: "testStore/deleteDownloadData",
      getDownloadStatus: "testStore/getDownloadStatus",
    }),

    async downloadTestResource(newUrl) {
      try {
        if (!newUrl) {
          throw new Error("Invalid URL. Cannot trigger download.");
        }

        console.log("Initiating download for URL:", newUrl);

        // Call the download API
        const res = await this.downloadTestPaper(newUrl);

        console.log("Download response:", res.request_id);

        if (!res || !res.request_id) {
          throw new Error("Invalid response from downloadTestPaper: Missing 'request_id'.");
        }

        this.requestIds.push(res.request_id);
        this.checkDownloadUpdate();
      } catch (error) {
        console.error("Error during test resource download:", error);
        this.isLoading = false;
      }
    },

    checkDownloadUpdate() {
      if (this.testStatusTimeout) {
        clearInterval(this.testStatusTimeout);
        this.testStatusTimeout = null;
      }

      if (this.requestIds.length) {
        this.testStatusTimeout = setInterval(() => {
          this.getTestDownloadStatus();
        }, 10000); // 10 seconds interval
      } else {
        console.warn("No request IDs to monitor.");
      }
    },

    async getTestDownloadStatus() {
      try {
        console.log("Fetching download status for:", this.requestIds);
        const additionalDetails = encodeURIComponent(this.$route.query.testData)
        

        const reqData = {

          request_ids: this.requestIds,
          download_types: [
          constants.DOWNLOAD_TYPES.TEST_REPORT,
          constants.DOWNLOAD_TYPES.TEST_PAPER,
          constants.DOWNLOAD_TYPES.SOLUTION_PAPER,
          ],
        };
        if (additionalDetails) {
          reqData.additional_details = additionalDetails;
        }
        const res = await this.getDownloadStatus(reqData);
        console.log("Download status response:", res);

        const completedRequest = res.find(
          (r) => r.request_id === this.requestIds[0] && r.status === constants.DOWNLOAD_STATUS.COMPLETED
        );

        if (completedRequest) {
          await this.downloadTestURL({
            url: completedRequest.url,
            request_id: completedRequest.request_id,
          });

          this.requestIds = [];
          clearInterval(this.testStatusTimeout);
          this.testStatusTimeout = null;

          // Update the message and display the success message
          this.loadingMessage = "";
          this.isLoading = false;
          this.downloadComplete = true;
        }
      } catch (error) {
        console.error("Error fetching download status:", error);
      }
    },

    async downloadTestURL(item) {
      try {
        const additionalDetails = encodeURIComponent(this.$route.query.testData)
        if (!item.url || !item.request_id) {
          throw new Error("Invalid download data. URL or request ID is missing.");
        }

        console.log("Downloading file from:", item.url);

        // Open the file for download
        window.open(item.url, "_self");

        const requestData = { request_id: item.request_id };
        
        if (additionalDetails) {
          requestData.additional_details = additionalDetails;
        }
        
        // Call deleteDownloadData immediately
        await this.deleteDownloadData(requestData);
        // await this.deleteDownloadData({ request_id: item.request_id });
        console.log("Download data cleared successfully.");

        // Update the message and display the success message
        this.loadingMessage = ""; // Clear the loading message
        this.isLoading = false;   // Hide the loader
        this.downloadComplete = true; // Show the success message

      
      } catch (error) {
        console.error("Error in downloadTestURL:", error);
      }
    },

    closeTab() {
      console.log("Closing the tab...");
      window.close();
    },
  },
  beforeDestroy() {
    if (this.testStatusTimeout) {
      clearInterval(this.testStatusTimeout);
    }
  },
};
</script>

<style scoped>
.download-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  text-align: center;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  color: #555;
}

.loading-text {
  margin-top: 16px;
  font-size: 20px;
  font-weight: bold;
  color: #333;
}

.success-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  color: #4caf50; /* Success green */
}

.success-text {
  margin-top: 16px;
  font-size: 22px;
  font-weight: bold;
}

.close-tab-btn {
  margin-top: 24px;
  font-size: 16px;
}
</style>
